import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

import { Button, ButtonProps, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { useCalcAvailableViewportHeight } from '@/hooks/useCalcAvailableViewportHeight';
import { usePathSet } from '@/hooks/usePathSet';
import { usePlotsForProject } from '@/pages/shared/hooks/usePlotsForProject';
import { buildPath } from '@/utils/buildPath';

import { usePlotId } from '../../../../../../../shared/hooks/usePlotId';
import { PlotBoundaryEditor } from '../../../../components/PlotBoundaryEditor';
import { usePlotForm } from '../../../../hooks/usePlotForm';
import { TitleBar } from '../../components/TitleBar';

export const EditPlotBoundaryPage = () => {
  const { projectId } = useParams();
  const { t } = useTranslation();
  const { availableHeight } = useCalcAvailableViewportHeight();
  const pathSet = usePathSet();

  const form = usePlotForm();

  const [plotType, plotName, multiPolygon] = form.watch(['plotType', 'name', 'multiPolygon']);

  const plotId = usePlotId();

  const plots = usePlotsForProject().data.results;

  if (!plotName) {
    throw Error('No plot name received.');
  }

  if (!plotType) {
    throw Error('No plot type received.');
  }

  if (!multiPolygon) {
    throw Error('No plot multiPolygon received.');
  }

  const editPath = projectId
    ? buildPath(pathSet.editPlot, { pathParams: { plotId, projectId } })
    : buildPath(pathSet.editPlotOrg, { pathParams: { plotId } });

  return (
    <Stack style={{ height: `${availableHeight}px` }}>
      <TitleBar title={t('landSteward.plot.edit.editPlotBoundaries')} />

      <PlotBoundaryEditor
        existingPlots={plots.filter((plot) => plot.id !== plotId)}
        ForwardButton={(props: ButtonProps) => (
          <Button data-testid='save-button' asChild {...props}>
            <Link to={editPath}>{t('global.ui.buttons.save')}</Link>
          </Button>
        )}
        BackButton={(props: ButtonProps) => (
          <Button variant='outline' asChild {...props}>
            <Link to={editPath}>{t('global.ui.buttons.back')}</Link>
          </Button>
        )}
      />
    </Stack>
  );
};
